/**
 * 🗑️ Delete Corporate Account Modal
 * Modal for permanently deleting a corporate account with safety checks
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  TextField,
} from '@mui/material';
import { Close, Warning, Delete } from '@mui/icons-material';
import {
  corporateAccountsService,
  CorporateAccount,
} from '@/services/corporateAccountsService';
import toast from 'react-hot-toast';

interface DeleteCorporateAccountModalProps {
  open: boolean;
  account: CorporateAccount | null;
  onClose: () => void;
  onDelete: () => void;
}

export const DeleteCorporateAccountModal: React.FC<DeleteCorporateAccountModalProps> = ({
  open,
  account,
  onClose,
  onDelete,
}) => {
  const [confirmText, setConfirmText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    setError('');

    if (confirmText !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    if (!account) return;

    setSubmitting(true);
    try {
      const response = await corporateAccountsService.deleteCorporateAccount(account.id);

      if (response.success) {
        toast.success(`${account.companyName} has been permanently deleted`);
        onDelete();
        handleClose();
      } else {
        // Handle specific error cases from backend
        if (response.data?.bookingCount) {
          setError(
            `${response.error}\n\nThis account has ${response.data.bookingCount} associated bookings. ${response.data.suggestion || 'Consider suspending instead.'}`
          );
        } else {
          setError(response.error || 'Failed to delete account');
        }
      }
    } catch (error) {
      console.error('Failed to delete account:', error);
      setError('Failed to delete account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setConfirmText('');
    setError('');
    onClose();
  };

  if (!account) return null;

  const isConfirmed = confirmText === 'DELETE';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Delete color="error" />
            <Typography variant="h6">Delete Corporate Account</Typography>
          </Box>
          <IconButton onClick={handleClose} disabled={submitting}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="error" icon={<Warning />} sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>Warning:</strong> This action is permanent and cannot be undone!
          </Typography>
        </Alert>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            Account to be deleted:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Company: <strong>{account.companyName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Email: <strong>{account.billingEmail}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Current Balance:{' '}
            <strong>
              {account.currency === 'NGN' ? '₦' : '$'}
              {account.currentBalance?.toLocaleString()}
            </strong>
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight="medium" gutterBottom>
            Safety Checks:
          </Typography>
          <Typography variant="caption" component="div">
            • Cannot delete accounts with bookings (active, completed, or cancelled)
          </Typography>
          <Typography variant="caption" component="div">
            • Cannot delete accounts with non-zero balance
          </Typography>
          <Typography variant="caption" component="div">
            • Consider suspending the account instead if you need to preserve history
          </Typography>
        </Alert>

        <Box>
          <Typography variant="body2" gutterBottom>
            Type <strong>DELETE</strong> to confirm permanent deletion:
          </Typography>
          <TextField
            fullWidth
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            disabled={submitting}
            placeholder="DELETE"
            error={!!error && error.includes('type DELETE')}
            helperText={error && error.includes('type DELETE') ? error : ''}
          />
        </Box>

        {error && !error.includes('type DELETE') && (
          <Alert severity="error" sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              {error}
            </Typography>
          </Alert>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleSubmit}
          disabled={submitting || !isConfirmed}
          startIcon={<Delete />}
        >
          {submitting ? 'Deleting...' : 'Delete Permanently'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
